import { formatCredits } from "@models/billing";
import BoltIcon from "@mui/icons-material/BoltRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import SwapHorizIcon from "@mui/icons-material/SwapHorizRounded";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useState } from "react";
import { useBillingPlan } from "../hooks/useBilling";
import { TopUpModal } from "./TopUpModal";
import { UpgradeModal } from "./UpgradeModal";

interface OutOfCreditsCardProps {
  required: number;
  available: number;
  onDismiss: () => void;
  /** Noun for the rejected unit of work — "turn" in chat, "run" in the
   *  workbench. Defaults to "turn" so chat usage is unchanged. */
  actionNoun?: string;
  /** Show the chat-only "Use Sonnet (5× cheaper)" model-swap CTA. Off
   *  for non-chat surfaces (e.g. workbench) that don't own the chat
   *  model picker. Defaults to true. */
  showModelSwap?: boolean;
}

/**
 * Panel rendered when a unit of work was rejected with
 * `insufficient_credits`. CTAs:
 *
 *   • Top up      — opens TopUpModal (Pro/Team only).
 *   • Upgrade     — opens UpgradeModal (anyone).
 *   • Use Sonnet  — flips the chat model picker if on Opus
 *                   (chat-only; gated by `showModelSwap`).
 *
 * Uses Paper + a warning border so the card is visually distinct from the
 * regular activity rows but doesn't slam-dunk a red error. Shared by the
 * chat composer and the workbench run-detail surface.
 */
export function OutOfCreditsCard({
  required,
  available,
  onDismiss,
  actionNoun = "turn",
  showModelSwap = true,
}: OutOfCreditsCardProps) {
  const [topupOpen, setTopupOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { data: plan } = useBillingPlan();
  const selectedModel = useChatStore((s) => s.selectedModel);
  const setSelectedModel = useChatStore((s) => s.setSelectedModel);

  const onOpus = showModelSwap && selectedModel === "opus";
  const isTrial = plan?.id === "trial";
  // Trial users can't buy top-ups — they have to upgrade first.
  const canTopUp = !isTrial && plan?.id !== "none";

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderColor: "warning.main",
          bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
          position: "relative",
        }}
      >
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Dismiss"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "text.secondary",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "warning.main",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <BoltIcon sx={{ fontSize: 20, color: "warning.contrastText" }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography sx={{ fontWeight: 700 }}>
              You're out of credits
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              That {actionNoun} would cost ~{formatCredits(required)} credits.
              You have {formatCredits(available)} left.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
            >
              {canTopUp && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<BoltIcon />}
                  onClick={() => setTopupOpen(true)}
                >
                  Top up
                </Button>
              )}
              <Button
                variant={canTopUp ? "outlined" : "contained"}
                size="small"
                onClick={() => setUpgradeOpen(true)}
              >
                {isTrial ? "Upgrade to Pro" : "Change plan"}
              </Button>
              {onOpus && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => {
                    setSelectedModel("sonnet");
                    onDismiss();
                  }}
                >
                  Use Sonnet (5× cheaper)
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <TopUpModal open={topupOpen} onClose={() => setTopupOpen(false)} />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}
